import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { 
  Button, 
  Modal, 
  Table, 
  Pagination, 
  Input, 
  Tag, 
  message, 
  Space, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Select,
  Badge,
  Popconfirm,
  Tooltip,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  KeyOutlined, 
  MailOutlined, 
  QqOutlined, 
  DeleteOutlined, 
  UserDeleteOutlined, 
  UserAddOutlined, 
  SettingOutlined, 
  SearchOutlined, 
  TeamOutlined, 
  UserOutlined, 
  StopOutlined, 
  LoginOutlined, 
  LogoutOutlined 
} from '@ant-design/icons';
import './UserManagement.css';

const { Title, Text } = Typography;
const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState(''); // 新增 email
  const [qq, setQq] = useState(''); // 增加 QQ号
  const [newEmail, setNewEmail] = useState(''); // 用于保存新邮箱
  const [newPassword, setNewPassword] = useState(''); // 用于保存新密码
  const [newQq, setNewQq] = useState(''); // 用于保存新 QQ 号
  const [newRole, setNewRole] = useState(''); // 用于保存新角色
  const [role, setRole] = useState('user');
  const [searchTerm, setSearchTerm] = useState(''); // 搜索关键词
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false); // 修改密码模态框
  const [showEmailModal, setShowEmailModal] = useState(false); // 修改邮箱模态框
  const [showQqModal, setShowQqModal] = useState(false); // 修改QQ号模态框
  const [showRoleModal, setShowRoleModal] = useState(false); // 修改角色模态框
  const [showParentUserModal, setShowParentUserModal] = useState(false); // 查看主账号模态框
  const [showMultiLoginModal, setShowMultiLoginModal] = useState(false); // 多点登录确认模态框
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [parentUserInfo, setParentUserInfo] = useState(null); // 主账号信息
  const [ramUserInfo, setRamUserInfo] = useState(null); // RAM账号信息
  const [multiLoginAction, setMultiLoginAction] = useState(null); // 多点登录操作信息


  const limit = 10; // 每页显示的用户数量

  // 获取所有用户
  const fetchUsers = async (page = 1, search = '') => {
    try {
      const response = await API.get(`/api/admin/users`, {
        params: { page, limit, search },
      });
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
    } catch (error) {
      console.error('获取用户列表失败:', error);
    }
  };

  // 处理搜索输入框变化（仅更新状态，不触发搜索）
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // 触发搜索
  const handleSearch = () => {
    setCurrentPage(1); // 重置到第一页
    fetchUsers(1, searchTerm);
  };



  // 清空搜索
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchUsers(1, '');
  };

  // 显示消息提示
  const showMessage = (text, type) => {
    if (type === 'success') {
      message.success(text);
    } else if (type === 'error') {
      message.error(text);
    } else if (type === 'warning') {
      message.warning(text);
    } else {
      message.info(text);
    }
  };

  // 创建新用户
  const handleCreateUser = async () => {
    try {
      await API.post('/api/admin/create-user', {
        username,
        password,
        email,  
        role,
        qq,
      });
      showMessage('用户创建成功', 'success');
      fetchUsers(currentPage, searchTerm);
      handleCloseModal();
    } catch (error) {
      console.error('创建用户失败:', error);
      showMessage('创建用户失败', 'error');
    }
  };

  // 打开模态框
  const handleShowModal = () => setShowModal(true);

  // 关闭模态框
  const handleCloseModal = () => {
    setShowModal(false);
    setUsername('');
    setPassword('');
    setEmail('');  
    setQq('');  // 清空 QQ号
    setRole('user');
  };



  // 删除用户
  const handleDeleteUser = async () => {
    try {
      await API.delete(`/api/admin/users/${selectedUserId}`);
      showMessage('用户删除成功', 'success');
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('删除用户失败:', error);
      showMessage('删除用户失败', 'error');
    }
  };

  // 打开修改密码模态框
  const handleShowPasswordModal = (userId, username) => {
    setSelectedUserId(userId);
    setUsername(username); 
    setNewPassword(''); 
    setShowPasswordModal(true);
  };

  // 关闭修改密码模态框
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setNewPassword('');
    setUsername('');
  };

  // 修改密码
  const handleChangePassword = async () => {
    try {
      await API.put(`/api/admin/change-password`, {
        username,  
        newPassword, 
      });
      showMessage('密码修改成功', 'success');
      handleClosePasswordModal();
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('修改密码失败:', error);
      showMessage('修改密码失败', 'error');
    }
  };

  // 打开修改邮箱模态框
  const handleShowEmailModal = (userId, username) => {
    setSelectedUserId(userId);
    setUsername(username); 
    setNewEmail(''); 
    setShowEmailModal(true);
  };

  // 关闭修改邮箱模态框
  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setNewEmail('');
    setUsername('');
  };

  // 修改邮箱
  const handleChangeEmail = async () => {
    try {
      await API.put(`/api/admin/change-email`, {
        username,  
        newEmail, 
      });
      showMessage('邮箱修改成功', 'success');
      handleCloseEmailModal();
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('修改邮箱失败:', error);
      showMessage('修改邮箱失败', 'error');
    }
  };

  // 打开修改QQ号模态框
  const handleShowQqModal = (userId, username) => {
    setSelectedUserId(userId);
    setUsername(username); 
    setNewQq(''); 
    setShowQqModal(true);
  };

  // 关闭修改QQ号模态框
  const handleCloseQqModal = () => {
    setShowQqModal(false);
    setNewQq('');
    setUsername('');
  };

  // 修改QQ号
  const handleChangeQq = async () => {
    try {
      await API.put(`/api/admin/change-qq`, {
        username,  
        newQq, 
      });
      showMessage('QQ号修改成功', 'success');
      handleCloseQqModal();
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('修改QQ号失败:', error);
      showMessage('修改QQ号失败', 'error');
    }
  };

  // 打开修改角色模态框
  const handleShowRoleModal = (userId, username, currentRole) => {
    setSelectedUserId(userId);
    setUsername(username); 
    setNewRole(currentRole); // 设置当前角色为默认值
    setShowRoleModal(true);
  };

  // 关闭修改角色模态框
  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setNewRole('');
    setUsername('');
  };

  // 修改角色
  const handleChangeRole = async () => {
    try {
      // 获取当前用户信息
      const currentUserResponse = await API.get('/api/admin/current-user');
      const currentAdmin = currentUserResponse.data.username;
      
      // 如果尝试修改的是当前登录的管理员账号
      if (username === currentAdmin) {
        showMessage('不能修改自己的角色', 'error');
        return;
      }

      await API.put(`/api/admin/change-role`, {
        username,  
        newRole, 
      });
      showMessage('用户角色修改成功', 'success');
      handleCloseRoleModal();
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('修改角色失败:', error);
      if (error.response?.data?.message) {
        showMessage(error.response.data.message, 'error');
      } else {
        showMessage('修改角色失败', 'error');
      }
    }
  };

  // 切换RAM创建权限
  const handleToggleRAMPermission = async (userId, currentPermission, username) => {
    try {
      const newPermission = !currentPermission;
      await API.put(`/api/admin/users/${userId}/ram-permission`, {
        canCreateRAM: newPermission
      });
      showMessage(
        `已${newPermission ? '开启' : '关闭'}用户 ${username} 的RAM账号创建权限`,
        'success'
      );
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('修改RAM创建权限失败:', error);
      if (error.response?.data?.message) {
        showMessage(error.response.data.message, 'error');
      } else {
        showMessage('修改RAM创建权限失败', 'error');
      }
    }
  };

  // 查看RAM账号的主账号信息
  const handleShowParentUser = async (userId) => {
    try {
      const response = await API.get(`/api/admin/users/${userId}/parent`);
      setRamUserInfo(response.data.ramUser);
      setParentUserInfo(response.data.parentUser);
      setShowParentUserModal(true);
    } catch (error) {
      console.error('获取主账号信息失败:', error);
      if (error.response?.data?.message) {
        showMessage(error.response.data.message, 'error');
      } else {
        showMessage('获取主账号信息失败', 'error');
      }
    }
  };

  // 关闭查看主账号模态框
  const handleCloseParentUserModal = () => {
    setShowParentUserModal(false);
    setParentUserInfo(null);
    setRamUserInfo(null);
  };

  // 格式化RAM权限显示
  const formatRamPermissions = (permissions) => {
    if (!permissions || typeof permissions !== 'object') {
      return '无特殊权限';
    }

    const permissionMap = {
      canViewIPList: '查看IP',
      canViewBigWallTraffic: '大墙流量', 
      canViewIPConnections: 'IP连接',
      canViewSmallWallLogs: '小墙流量',
      canViewAttackRecords: '攻击记录',
      canManageIPs: 'IP策略',
      canExportReports: '导出报告',
      canModifySettings: '账号设置'
    };

    const enabledPermissions = [];
    
    Object.keys(permissions).forEach(key => {
      if (key !== '_id' && permissions[key] === true && permissionMap[key]) {
        enabledPermissions.push(permissionMap[key]);
      }
    });

    return enabledPermissions.length > 0 ? enabledPermissions.join(', ') : '无特殊权限';
  };

  // 修改用户状态
  const handleStatusChange = async (userId, newStatus, currentUsername) => {
    try {
      // 获取当前用户信息
      const currentUserResponse = await API.get('/api/admin/current-user');
      const currentAdmin = currentUserResponse.data.username;
      
      // 如果尝试封禁的是当前登录的管理员账号
      if (currentUsername === currentAdmin) {
        showMessage('不能封禁自己的账号', 'error');
        return;
      }

      await API.put(`/api/admin/users/${userId}/status`, {
        status: newStatus
      });
      showMessage(
        newStatus === 'active' ? '用户已启用' : '用户已禁用',
        'success'
      );
      fetchUsers(currentPage, searchTerm);
    } catch (error) {
      console.error('更新用户状态失败:', error);
      showMessage('更新用户状态失败', 'error');
    }
  };

  // 切换多点登录权限
  const handleToggleMultiLogin = async (userId, currentPermission, username) => {
    setSelectedUserId(userId);
    setUsername(username);
    setMultiLoginAction({
      userId,
      currentPermission,
      username,
      newPermission: !currentPermission
    });
    setShowMultiLoginModal(true);
  };

  // 确认多点登录权限变更
  const handleConfirmMultiLogin = async () => {
    try {
      const { userId, newPermission, username } = multiLoginAction;
      
      const response = await API.put(`/api/admin/users/${userId}/multi-login`, {
        allowMultipleLogin: newPermission
      });
      
      showMessage(
        `已${newPermission ? '开启' : '关闭'}用户 ${username} 的多点登录权限${response.data.oldSetting !== undefined ? ` (原设置: ${response.data.oldSetting ? '开启' : '关闭'})` : ''}`,
        'success'
      );
      
      // 如果响应包含额外信息，也显示出来
      if (response.data.kickedSessionsCount > 0) {
        setTimeout(() => {
          showMessage(
            `因禁用多点登录，已踢出用户 ${username} 的 ${response.data.kickedSessionsCount} 个会话`,
            'success'
          );
        }, 3500);
      }
      
      fetchUsers(currentPage, searchTerm);
      setShowMultiLoginModal(false);
      setMultiLoginAction(null);
    } catch (error) {
      console.error('修改多点登录权限失败:', error);
      if (error.response?.data?.message) {
        showMessage(error.response.data.message, 'error');
      } else {
        showMessage('修改多点登录权限失败', 'error');
      }
      setShowMultiLoginModal(false);
      setMultiLoginAction(null);
    }
  };

  // 关闭多点登录确认模态框
  const handleCloseMultiLoginModal = () => {
    setShowMultiLoginModal(false);
    setMultiLoginAction(null);
  };

  // 页面加载时获取用户列表
  useEffect(() => {
    fetchUsers(currentPage, ''); // 初始加载时不带搜索条件
  }, [currentPage]);

  // 处理分页
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchUsers(pageNumber, searchTerm);
  };

  return (
    <div className="user-management-container">
      {/* 页面头部 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>用户管理</Title>
      </div>

      {/* 顶部操作栏 */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col flex="1" style={{ maxWidth: 600 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="搜索用户名/邮箱/QQ/角色/状态/账户类型/多点登录"
                value={searchTerm}
                onChange={handleSearchChange}
                onPressEnter={handleSearch}
                suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
              />
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              {searchTerm && (
                <Button onClick={handleClearSearch}>
                  清空
                </Button>
              )}
            </Space.Compact>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleShowModal}
            >
              创建新用户
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 用户列表卡片 */}
      <Card>
        <Table
          dataSource={users}
          rowKey="_id"
          pagination={false}
          scroll={{ x: 1400 }}
          columns={[
            {
              title: '用户名',
              dataIndex: 'username',
              key: 'username',
              width: 120,
              fixed: 'left'
            },
            {
              title: '邮箱',
              dataIndex: 'email',
              key: 'email',
              width: 200,
              ellipsis: true
            },
            {
              title: 'QQ号',
              dataIndex: 'qq',
              key: 'qq',
              width: 120
            },
            {
              title: '账户类型',
              dataIndex: 'accountType',
              key: 'accountType',
              width: 100,
              align: 'center',
              render: (accountType) => (
                <Tag color={accountType === 'main' ? 'green' : 'blue'}>
                  {accountType === 'main' ? '主账号' : accountType === 'ram' ? 'RAM账号' : '未知'}
                </Tag>
              )
            },
            {
              title: '角色',
              dataIndex: 'role',
              key: 'role',
              width: 120,
              align: 'center',
              render: (role) => (
                <Tag color={
                  role === 'admin' ? 'red' : 
                  role === 'wlj-user' ? 'orange' : 
                  role === 'pending-verification' ? 'default' : 
                  'blue'
                }>
                  {role === 'admin' ? '管理员' : 
                   role === 'wlj-user' ? '物理机用户' : 
                   role === 'pending-verification' ? '待审核用户' :
                   '普通用户'}
                </Tag>
              )
            },
            {
              title: 'RAM权限',
              key: 'ramPermission',
              width: 100,
              align: 'center',
              render: (_, record) => {
                if (record.accountType === 'main') {
                  return (
                    <Tag color={record.canCreateRAM ? 'green' : 'default'}>
                      {record.canCreateRAM ? '可创建' : '禁止创建'}
                    </Tag>
                  );
                } else {
                  return (
                    <Tag color="default">
                      不适用
                    </Tag>
                  );
                }
              }
            },
            {
              title: '多点登录',
              dataIndex: 'allowMultipleLogin',
              key: 'allowMultipleLogin',
              width: 100,
              align: 'center',
              render: (allowMultipleLogin) => (
                <Tag color={allowMultipleLogin ? 'green' : 'default'}>
                  {allowMultipleLogin ? '允许' : '禁止'}
                </Tag>
              )
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 80,
              align: 'center',
              render: (status) => (
                <Badge 
                  status={status === 'active' ? 'success' : 'error'} 
                  text={status === 'active' ? '正常' : '禁用'}
                />
              )
            },
            {
              title: '操作',
              key: 'action',
              width: 400,
              fixed: 'right',
              render: (_, record) => (
                <Space wrap>
                  <Tooltip title={record.status === 'active' ? '禁用用户' : '启用用户'}>
                    <Button
                      type={record.status === 'active' ? 'default' : 'primary'}
                      size="small"
                      icon={record.status === 'active' ? <UserDeleteOutlined /> : <UserAddOutlined />}
                      onClick={() => handleStatusChange(record._id, record.status === 'active' ? 'disabled' : 'active', record.username)}
                    />
                  </Tooltip>
                  {record.accountType === 'main' && (
                    <Tooltip title={record.canCreateRAM ? '禁止创建RAM账号' : '允许创建RAM账号'}>
                      <Button
                        type={record.canCreateRAM ? 'default' : 'primary'}
                        size="small"
                        icon={record.canCreateRAM ? <StopOutlined /> : <UserOutlined />}
                        onClick={() => handleToggleRAMPermission(record._id, record.canCreateRAM, record.username)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title={record.allowMultipleLogin ? '禁止多点登录' : '允许多点登录'}>
                    <Button
                      type="default"
                      size="small"
                      icon={record.allowMultipleLogin ? <LogoutOutlined /> : <LoginOutlined />}
                      onClick={() => handleToggleMultiLogin(record._id, record.allowMultipleLogin, record.username)}
                    />
                  </Tooltip>
                  {record.accountType === 'ram' && (
                    <Tooltip title="查看主账号">
                      <Button
                        type="default"
                        size="small"
                        icon={<TeamOutlined />}
                        onClick={() => handleShowParentUser(record._id)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="修改密码">
                    <Button
                      type="default"
                      size="small"
                      icon={<KeyOutlined />}
                      onClick={() => handleShowPasswordModal(record._id, record.username)}
                    />
                  </Tooltip>
                  <Tooltip title="修改邮箱">
                    <Button
                      type="default"
                      size="small"
                      icon={<MailOutlined />}
                      onClick={() => handleShowEmailModal(record._id, record.username)}
                    />
                  </Tooltip>
                  <Tooltip title="修改QQ号">
                    <Button
                      type="default"
                      size="small"
                      icon={<QqOutlined />}
                      onClick={() => handleShowQqModal(record._id, record.username)}
                    />
                  </Tooltip>
                  <Tooltip title="修改角色">
                    <Button
                      type="default"
                      size="small"
                      icon={<SettingOutlined />}
                      onClick={() => handleShowRoleModal(record._id, record.username, record.role)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="确定要删除该用户吗？"
                    description="删除后将连同其绑定的IP地址一并删除，且无法恢复。"
                    onConfirm={() => handleDeleteUser()}
                    onOpenChange={(open) => {
                      if (open) {
                        setSelectedUserId(record._id);
                      }
                    }}
                  >
                    <Tooltip title="删除用户">
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              )
            }
          ]}
        />
      </Card>

      {/* 分页 */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Pagination
          current={currentPage}
          total={totalPages * limit}
          pageSize={limit}
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper
          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`}
        />
      </div>

      {/* 创建新用户的模态框 */}
      <Modal
        title="创建新用户"
        open={showModal}
        onOk={handleCreateUser}
        onCancel={handleCloseModal}
        okText="创建用户"
        cancelText="取消"
        width={600}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>用户名 *</label>
            <Input
              placeholder="输入用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>邮箱 *</label>
            <Input
              type="email"
              placeholder="输入邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>密码 *</label>
            <Input.Password
              placeholder="输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>角色 *</label>
            <Select value={role} onChange={setRole} style={{ width: '100%' }}>
              <Option value="user">普通用户</Option>
              <Option value="wlj-user">物理机用户</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>QQ号</label>
            <Input
              placeholder="输入QQ号"
              value={qq}
              onChange={(e) => setQq(e.target.value)}
            />
          </div>
        </div>
      </Modal>



      {/* 修改密码的模态框 */}
      <Modal
        title="修改密码"
        open={showPasswordModal}
        onOk={handleChangePassword}
        onCancel={handleClosePasswordModal}
        okText="确认修改"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>用户名</label>
            <Input
              value={username}
              disabled
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>新密码 *</label>
            <Input.Password
              placeholder="输入新密码"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* 修改邮箱的模态框 */}
      <Modal
        title="修改邮箱"
        open={showEmailModal}
        onOk={handleChangeEmail}
        onCancel={handleCloseEmailModal}
        okText="确认修改"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>用户名</label>
            <Input
              value={username}
              disabled
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>新邮箱 *</label>
            <Input
              type="email"
              placeholder="请输入新邮箱"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* 修改QQ号的模态框 */}
      <Modal
        title="修改QQ号"
        open={showQqModal}
        onOk={handleChangeQq}
        onCancel={handleCloseQqModal}
        okText="确认修改"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>用户名</label>
            <Input
              value={username}
              disabled
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>新QQ号 *</label>
            <Input
              placeholder="请输入新QQ号"
              value={newQq}
              onChange={(e) => setNewQq(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* 修改角色的模态框 */}
      <Modal
        title="修改用户角色"
        open={showRoleModal}
        onOk={handleChangeRole}
        onCancel={handleCloseRoleModal}
        okText="确认修改"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>用户名</label>
            <Input
              value={username}
              disabled
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>新角色 *</label>
            <Select value={newRole} onChange={setNewRole} style={{ width: '100%' }}>
              <Option value="pending-verification">待审核用户</Option>
              <Option value="user">普通用户</Option>
              <Option value="wlj-user">物理机用户</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </div>
        </div>
      </Modal>

      {/* 查看RAM账号主账号信息的模态框 */}
      <Modal
        title={
          <span>
            <TeamOutlined style={{ marginRight: 8 }} />
            RAM账号主账号信息
          </span>
        }
        open={showParentUserModal}
        onCancel={handleCloseParentUserModal}
        footer={[
          <Button key="close" onClick={handleCloseParentUserModal}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {ramUserInfo && parentUserInfo && (
          <div>
            {/* RAM账号信息 */}
            <div style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginBottom: 16 }}>
                <Tag color="blue" style={{ marginRight: 8 }}>RAM账号</Tag>
                {ramUserInfo.username}
              </Title>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <p><Text strong>账号ID:</Text> {ramUserInfo._id}</p>
                  <p><Text strong>邮箱:</Text> {ramUserInfo.email || '未设置'}</p>
                  <p><Text strong>QQ号:</Text> {ramUserInfo.qq || '未设置'}</p>
                </Col>
                <Col span={12}>
                  <p><Text strong>账户类型:</Text> 
                    <Tag color="blue" style={{ marginLeft: 8 }}>RAM账号</Tag>
                  </p>
                  <p><Text strong>创建时间:</Text> {ramUserInfo.createdAt ? new Date(ramUserInfo.createdAt).toLocaleString() : '未知'}</p>
                  <p><Text strong>权限:</Text> {formatRamPermissions(ramUserInfo.ramPermissions)}</p>
                </Col>
              </Row>
            </div>

            <div style={{ height: 1, backgroundColor: '#f0f0f0', margin: '24px 0' }} />

            {/* 主账号信息 */}
            <div>
              <Title level={5} style={{ marginBottom: 16 }}>
                <Tag color="green" style={{ marginRight: 8 }}>主账号</Tag>
                {parentUserInfo.username}
              </Title>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <p><Text strong>账号ID:</Text> {parentUserInfo._id}</p>
                  <p><Text strong>邮箱:</Text> {parentUserInfo.email}</p>
                  <p><Text strong>QQ号:</Text> {parentUserInfo.qq || '未设置'}</p>
                </Col>
                <Col span={12}>
                  <p><Text strong>角色:</Text> 
                    <Tag 
                      color={parentUserInfo.role === 'admin' ? 'red' : 
                             parentUserInfo.role === 'wlj-user' ? 'orange' : 
                             parentUserInfo.role === 'pending-verification' ? 'default' : 
                             'blue'}
                      style={{ marginLeft: 8 }}
                    >
                      {parentUserInfo.role === 'admin' ? '管理员' : 
                       parentUserInfo.role === 'wlj-user' ? '物理机用户' : 
                       parentUserInfo.role === 'pending-verification' ? '待审核用户' : 
                       '普通用户'}
                    </Tag>
                  </p>
                  <p><Text strong>状态:</Text> 
                    <Badge 
                      status={parentUserInfo.status === 'active' ? 'success' : 'error'} 
                      text={parentUserInfo.status === 'active' ? '正常' : '禁用'}
                      style={{ marginLeft: 8 }}
                    />
                  </p>
                  <p><Text strong>创建时间:</Text> {new Date(parentUserInfo.createdAt).toLocaleString()}</p>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>

      {/* 多点登录权限确认模态框 */}
      <Modal
        title={
          <span>
            <LoginOutlined style={{ marginRight: 8 }} />
            {multiLoginAction?.newPermission ? '开启' : '关闭'}多点登录权限
          </span>
        }
        open={showMultiLoginModal}
        onOk={handleConfirmMultiLogin}
        onCancel={handleCloseMultiLoginModal}
        okText={`确认${multiLoginAction?.newPermission ? '开启' : '关闭'}`}
        okType={multiLoginAction?.newPermission ? 'primary' : 'default'}
        cancelText="取消"
        width={600}
      >
        {multiLoginAction && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 24 }}>
              <p><Text strong>用户名:</Text> {multiLoginAction.username}</p>
              <p>
                <Text strong>当前状态:</Text> 
                <Tag 
                  color={multiLoginAction.currentPermission ? 'green' : 'default'} 
                  style={{ marginLeft: 8 }}
                >
                  {multiLoginAction.currentPermission ? '允许多点登录' : '禁止多点登录'}
                </Tag>
              </p>
              <p>
                <Text strong>将要设置为:</Text> 
                <Tag 
                  color={multiLoginAction.newPermission ? 'green' : 'default'} 
                  style={{ marginLeft: 8 }}
                >
                  {multiLoginAction.newPermission ? '允许多点登录' : '禁止多点登录'}
                </Tag>
              </p>
            </div>
            
            {multiLoginAction.newPermission ? (
              <Alert
                message="开启多点登录"
                description="用户将可以在多个设备上同时保持登录状态，不会被互相踢下线。这对于需要在多个设备上工作的用户很有用。"
                type="info"
                showIcon
                icon={<LoginOutlined />}
              />
            ) : (
              <Alert
                message="关闭多点登录"
                description={
                  <div>
                    <p style={{ marginBottom: 8 }}>
                      用户将只能在一个设备上保持登录状态，新登录会踢出之前的会话。
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      <Text strong>注意：</Text>如果用户当前在多个设备上登录，
                      关闭此权限后会立即踢出除最新活动会话外的所有其他会话。
                    </p>
                  </div>
                }
                type="warning"
                showIcon
                icon={<LogoutOutlined />}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
